import { useCallback, useEffect, useState } from "react";

import {
  DataGrid,
  gridRowSelectionIdsSelector,
  useGridApiRef,
} from "@mui/x-data-grid";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { ulid } from "ulid";

import { useOutletApi } from "@/features/authentication";
import { SalesOrderDeliveryStatusEnum } from "@/features/sales-orders";
import { formatDate, formatNumber } from "@/features/shared";
import { CustomAsyncAutocomplete, ProductVariantNameCell } from "@/features/ui";

import { Button } from "@mui/material";

export const SelectSalesOrderProductVariantSection = () => {
  const { control } = useFormContext();
  const apiRef = useGridApiRef();
  const [salesOrder, setSalesOrder] = useState(null);
  const [productVariants, setProductVariants] = useState([]);
  const [loadingProductVariants, setLoadingProductVariants] = useState(false);
  const [
    selectedSalesOrderProductVariants,
    setSelectedSalesOrderProductVariants,
  ] = useState([]);

  const { append, update } = useFieldArray({
    control,
    name: "deliveryOrderProductVariants",
  });

  const [buyer, seller, warehouse, deliveryOrderProductVariants] = useWatch({
    control,
    name: ["buyer", "seller", "warehouse", "deliveryOrderProductVariants"],
    exact: true,
  });

  const outletApi = useOutletApi(seller?.id);

  const disableBrowseSalesOrder = !buyer || !warehouse;

  const fetchUndeliveredSalesOrders = useCallback(
    async (searchValue, { page, size } = {}) => {
      if (!buyer || !seller || !warehouse) return [];

      const unfinishedStatuses = [
        SalesOrderDeliveryStatusEnum.PENDING,
        SalesOrderDeliveryStatusEnum.PARTIALLY_DELIVERED,
      ].join(",");

      const {
        data: { data },
      } = await outletApi.get("sales-orders", {
        params: {
          page,
          size,
          deliveryStatus: unfinishedStatuses,
          buyerableId: buyer?.id,
          warehouseId: warehouse?.id,
          referenceNumber: searchValue || null,
        },
      });
      return data;
    },
    [buyer, seller, warehouse, outletApi]
  );

  const fetchSalesOrderProductVariants = useCallback(
    async (referenceNumber) => {
      if (!referenceNumber || !seller) return;

      setLoadingProductVariants(true);

      try {
        const {
          data: { data },
        } = await outletApi.get(
          `sales-orders/${referenceNumber}/sales-order-product-variants`,
          {
            params: {
              excludeFullyDelivered: true,
            },
          }
        );

        // 🔥 Adjust remaining quantity based on DOPV
        const adjustedProductVariants = data
          .map((salesOrderProductVariant) => {
            // Calculate total quantity for this variant across all DOPV entries
            // Only count newly-added items (not existing DO items) to avoid double-counting
            const totalDeliveryOrderQuantity = deliveryOrderProductVariants
              .filter(
                (deliveryOrderProductVariant) =>
                  deliveryOrderProductVariant.salesOrderProductVariantId ===
                    salesOrderProductVariant.id &&
                  deliveryOrderProductVariant.isNewlyAdded === true
              )
              .reduce((total, dopv) => total + dopv.quantity, 0);

            return {
              ...salesOrderProductVariant,
              adjustedRemainingQuantity:
                salesOrderProductVariant.quantity -
                salesOrderProductVariant.deliveredQuantity -
                totalDeliveryOrderQuantity, // Adjust based on total DOPV quantity
            };
          })
          .filter((variant) => variant.adjustedRemainingQuantity > 0); // 🚀 Keep only variants with available stock

        setProductVariants(adjustedProductVariants);
      } catch (error) {
        console.log(error);
      } finally {
        setLoadingProductVariants(false);
      }
    },
    [deliveryOrderProductVariants, seller, outletApi]
  );

  useEffect(() => {
    if (salesOrder) {
      fetchSalesOrderProductVariants(salesOrder.referenceNumber);
    } else {
      setProductVariants([]);
    }
    setSelectedSalesOrderProductVariants([]); // Reset selection when sales order changes
  }, [fetchSalesOrderProductVariants, salesOrder]);

  const columns = [
    {
      field: "name",
      headerName: "Nama",
      flex: 5,
      renderCell: ({ row: { name, sku } }) => (
        <ProductVariantNameCell name={name} sku={sku} />
      ),
    },
    {
      field: "adjustedRemainingQuantity",
      headerName: "Qty",
      type: "number",
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "unitOfMeasurement",
      headerName: "Unit",

      valueGetter: (_, row) => row.product.unitOfMeasurement.name,
    },
    {
      field: "note",
      headerName: "Catatan",
      flex: 3,
      valueGetter: (value) => value || "-",
    },
  ];

  const submitSelection = () => {
    // Use the already stored selected models - no need to filter again!
    const selectedProductVariants = selectedSalesOrderProductVariants;

    // Reset the state after submission
    setSalesOrder(null);
    setProductVariants([]);

    selectedProductVariants.forEach(
      ({
        id: salesOrderProductVariantId,
        name,
        sku,
        note,
        adjustedRemainingQuantity,
        product: {
          unitOfMeasurement: { name: unitOfMeasurement },
        },
      }) => {
        // 🔍 Check if the sales order product variant already exists in DOPV
        const existingDeliveryOrderProductVariantIndex =
          deliveryOrderProductVariants.findIndex(
            (deliveryOrderProductVariant) =>
              deliveryOrderProductVariant.salesOrderProductVariantId ===
              salesOrderProductVariantId
          );

        if (existingDeliveryOrderProductVariantIndex !== -1) {
          // ✅ Update existing delivery order product variant quantity
          update(existingDeliveryOrderProductVariantIndex, {
            ...deliveryOrderProductVariants[
              existingDeliveryOrderProductVariantIndex
            ],
            quantity:
              deliveryOrderProductVariants[
                existingDeliveryOrderProductVariantIndex
              ].quantity + adjustedRemainingQuantity, // Merge quantities
          });
        } else {
          // 🔥 Append as new record if not found
          append({
            id: ulid(),
            salesOrderProductVariantId: salesOrderProductVariantId,
            name,
            sku,
            quantity: adjustedRemainingQuantity,
            maxQuantity: adjustedRemainingQuantity,
            unitOfMeasurement,
            // Carried from the order line; still editable in the grid.
            note: note ?? null,
            isNewlyAdded: true, // Flag to identify newly added variants
          });
        }
      }
    );
  };

  return (
    <>
      <CustomAsyncAutocomplete
        value={salesOrder}
        onChange={setSalesOrder}
        disabled={disableBrowseSalesOrder}
        fetchOptions={fetchUndeliveredSalesOrders}
        getOptionLabel={(option) => option.referenceNumber}
        isOptionEqualToValue={(option, selectedOption) =>
          option.id === selectedOption.id
        }
        renderOption={(props, option) => {
          const { id, referenceNumber, transactionDate } = option;
          return (
            <li {...props} key={id}>
              <div>
                <div>{referenceNumber}</div>
                <div style={{ fontSize: "0.75rem" }}>
                  {formatDate(transactionDate)}
                </div>
              </div>
            </li>
          );
        }}
        TextFieldProps={{
          helperText: disableBrowseSalesOrder
            ? "Pilih Pelanggan dan Gudang terlebih dahulu"
            : null,
          InputProps: {
            placeholder: "Cari nomor referensi pesanan",
          },
        }}
      />
      <DataGrid
        getRowHeight={() => "auto"}
        apiRef={apiRef}
        columns={columns}
        rows={productVariants}
        loading={loadingProductVariants}
        checkboxSelection
        initialState={{
          sorting: {
            sortModel: [{ field: "name", sort: "asc" }],
          },
        }}
        onRowSelectionModelChange={() => {
          const selectedModels = Array.from(
            gridRowSelectionIdsSelector(apiRef.current.state).values()
          );
          setSelectedSalesOrderProductVariants(selectedModels);
        }}
      />
      <Button
        fullWidth
        variant="contained"
        disabled={selectedSalesOrderProductVariants.length === 0}
        sx={{ mt: 1 }}
        onClick={submitSelection}
      >
        Tambah
      </Button>
    </>
  );
};
